import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import MessageIcon from "@mui/icons-material/Message";
import AssessmentIcon from "@mui/icons-material/Assessment";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import EventIcon from "@mui/icons-material/Event";
import CryptoJS from "crypto-js";

const AES_KEY = "7D3B8E69F1A84B7EA8A3F6792B44C926";
const AES_IV = "A1B2C3D4E5F60718";

const decryptText = (encryptedText) => {
  const key = CryptoJS.enc.Utf8.parse(AES_KEY);
  const iv = CryptoJS.enc.Utf8.parse(AES_IV);
  const decrypted = CryptoJS.AES.decrypt(encryptedText, key, {
    iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });
  return decrypted.toString(CryptoJS.enc.Utf8);
};

const AdminNavBar = ({ onOpenChat, onOpenChapterReport, onOpenEventReport }) => {
  const [adminName, setAdminName] = useState("");
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [reportMenuAnchor, setReportMenuAnchor] = useState(null);

  useEffect(() => {
    let session = localStorage.getItem("adminLogsessiondetails") || localStorage.getItem("adminsessiondetails");
    if (session) {
      try {
        const decrypted = decryptText(session);
        const parsed = JSON.parse(decrypted);
        if (parsed.StaffCode) setAdminName(parsed.StaffCode);
      } catch {
        setAdminName("");
      }
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("adminLogsessiondetails");
    localStorage.removeItem("adminsessiondetails");
    window.location.href = "/BasicAuth";
  };

  const handleReportMenuOpen = (event) => {
    setReportMenuAnchor(event.currentTarget);
  };

  const handleReportMenuClose = () => {
    setReportMenuAnchor(null);
  };

  const handleChapterReport = () => {
    handleReportMenuClose();
    if (onOpenChapterReport) {
      onOpenChapterReport();
    }
  };

  const handleEventReport = () => {
    handleReportMenuClose();
    if (onOpenEventReport) {
      onOpenEventReport();
    }
  };

  return (
    <>
      <AppBar
        position="static"
        color="inherit"
        elevation={2}
        sx={{
          borderRadius: 2,
          mb: 3,
          backgroundColor: "#fff",
          color: "text.primary",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            minHeight: 64,
            px: { xs: 2, sm: 3, md: 4 },
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: "primary.main",
                boxShadow: 2,
              }}
            >
              <AdminPanelSettingsIcon sx={{ color: "#fff", fontSize: 24 }} />
            </Avatar>
            <Box>
              <Typography
                variant="h6"
                component="div"
                sx={{
                  fontWeight: 600,
                  color: "primary.main",
                  fontSize: { xs: "0.95rem", sm: "1.1rem", md: "1.25rem" },
                  lineHeight: 1.2,
                }}
              >
                {adminName ? `Welcome, ${adminName}` : "Admin Panel"}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontSize: "0.75rem",
                  display: { xs: "none", sm: "block" },
                }}
              >
                Admin Dashboard
              </Typography>
            </Box>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title="Reports">
              <IconButton
                color="secondary"
                onClick={handleReportMenuOpen}
                sx={{
                  "&:hover": {
                    backgroundColor: "secondary.lighter",
                    transform: "scale(1.05)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                <AssessmentIcon />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={reportMenuAnchor}
              open={Boolean(reportMenuAnchor)}
              onClose={handleReportMenuClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <MenuItem onClick={() => { handleReportMenuClose(); if (onOpenChapterReport) onOpenChapterReport(true); }}>
                <ListItemIcon>
                  <GroupWorkIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primaryTypographyProps={{ fontSize: "0.75rem" }}>
                  All Chapters Members
                </ListItemText>
              </MenuItem>
              <MenuItem onClick={() => { handleReportMenuClose(); if (onOpenEventReport) onOpenEventReport(true); }}>
                <ListItemIcon>
                  <EventIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primaryTypographyProps={{ fontSize: "0.75rem" }}>
                  All Events Members
                </ListItemText>
              </MenuItem>
            </Menu>
            {onOpenChat && (
              <Tooltip title="Global Chat">
                <IconButton
                  color="primary"
                  onClick={onOpenChat}
                  sx={{
                    "&:hover": {
                      backgroundColor: "primary.lighter",
                      transform: "scale(1.05)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  <MessageIcon />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Logout">
              <IconButton
                color="error"
                onClick={() => setLogoutDialogOpen(true)}
                sx={{
                  "&:hover": {
                    backgroundColor: "error.lighter",
                    transform: "scale(1.05)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                <LogoutIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      <Dialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 600, color: "error.main" }}>
          Logout Confirmation
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" color="text.secondary">
            Are you sure you want to logout from the Admin Panel?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <Button
            onClick={() => setLogoutDialogOpen(false)}
            color="inherit"
            variant="outlined"
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleLogout}
            color="error"
            variant="contained"
            startIcon={<LogoutIcon />}
            sx={{ textTransform: "none" }}
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Typechecking props for AdminNavBar
AdminNavBar.propTypes = {
  onOpenChat: PropTypes.func,
  onOpenChapterReport: PropTypes.func, // Can accept boolean for "all" view
  onOpenEventReport: PropTypes.func, // Can accept boolean for "all" view
};

export default AdminNavBar; 